import {
    Box,
    Button,
    Container,
    Grid,
    Input,
    Textarea,
    Heading,
    Text,
    Field,
    Separator,
} from "@chakra-ui/react"
import ReCAPTCHA from "react-google-recaptcha"
import emailjs from "emailjs-com"
import { useState, useRef } from "react"
import { toaster } from "./ui/toaster"

export default function Contact() {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const recaptchaRef = useRef(null)
    const [fieldStates, setFieldStates] = useState({
        firstName: { touched: false, invalid: false },
        lastName: { touched: false, invalid: false },
        email: { touched: false, invalid: false },
        phone: { touched: false, invalid: false },
        carModel: { touched: false, invalid: false },
        yearModel: { touched: false, invalid: false },
        message: { touched: false, invalid: false },
    })

    // Validation functions
    const validateField = (name, value) => {
        switch (name) {
            case 'firstName':
            case 'lastName':
                return value.length >= 2 && value.length <= 30
            case 'email':
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            case 'phone':
                return /^\+?\d{10,15}$/.test(value)
            case 'carModel':
                return value.length >= 2 && value.length <= 75
            case 'yearModel':
                return /^\d{4}$/.test(value)
            case 'message':
                return value.length >= 10 && value.length <= 5000
            default:
                return true
        }
    }

    const handleBlur = (e) => {
        const { name, value } = e.target
        const isValid = validateField(name, value)
        
        setFieldStates(prev => ({
            ...prev,
            [name]: {
                touched: true,
                invalid: !isValid
            }
        }))
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        // Only validate if the field has been touched (blurred at least once)
        if (fieldStates[name].touched) {
            const isValid = validateField(name, value)
            setFieldStates(prev => ({
                ...prev,
                [name]: {
                    ...prev[name],
                    invalid: !isValid
                }
            }))
        }
    }

    const validateAllFields = (form) => {
        let isValid = true
        const newFieldStates = { ...fieldStates }
        
        const fields = [
            'firstName', 'lastName', 'email', 'phone', 
            'carModel', 'yearModel', 'message'
        ]
        
        fields.forEach(field => {
            const value = form.elements[field].value
            const fieldValid = validateField(field, value)
            
            newFieldStates[field] = {
                touched: true,
                invalid: !fieldValid
            }
            
            if (!fieldValid) isValid = false
        })
        
        setFieldStates(newFieldStates)
        return isValid
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const form = e.target

        // Validate all fields
        if (!validateAllFields(form)) {
            toaster.create({
                title: "Validation Error",
                description: "Please fill out all fields correctly.",
                type: "error",
                duration: 10000,
            })
            return
        }

        // Validate reCAPTCHA
        if (!recaptchaRef.current.getValue()) {
            toaster.create({
                title: "Please complete the reCAPTCHA",
                description: "Please verify you're not a robot.",
                type: "error",
                duration: 10000,
            })
            return
        }

        setIsSubmitting(true)

        emailjs
        .sendForm("service_hwu0aah", "template_a6me96i", form, "VSZOTcqfB36NT3oeq")
            .then(
                () => {
                    toaster.create({
                        title: "Message sent!",
                        description: "We have received your message, and will get back to you shortly.",
                        type: "success",
                        duration: 15000,
                    })
                    form.reset()
                    recaptchaRef.current.reset()
                    setIsSubmitted(true)
                },
                (error) => {
                    toaster.create({
                        title: "Something went wrong",
                        description: "Unable to send your message. Please try again later.",
                        type: "error",
                        duration: 10000,
                    })
                    console.error(error)
                }
            )
            .finally(() => setIsSubmitting(false))
    }

    // Error messages for each field
    const getErrorMessage = (name) => {
        switch (name) {
            case 'firstName':
            case 'lastName':
                return 'Must be between 2-30 characters'
            case 'email':
                return 'Please enter a valid email address'
            case 'phone':
                return 'Please enter a valid phone number (10-15 digits, + allowed)'
            case 'carModel':
                return 'Must be between 2-75 characters'
            case 'yearModel':
                return 'Must be a 4-digit year'
            case 'message':
                return 'Must be between 10-5000 characters'
            default:
                return 'This field is required'
        }
    }

    return (
        <Box id="contact" bg="#F3F4F6" py={10}>
            <Container maxW="container.xl" px={{ base: 4, md: 8 }} mt={0}>
                <Heading size="5xl" fontWeight="medium" mb={2} mt={0}>
                    Contact
                </Heading>
                <Text fontSize="1xl">
                    Contact Chris via our website form.
                </Text>
                <Separator my={5} />
                <Box maxW={{ base: "100%", md: "60%" }}>
                <form onSubmit={handleSubmit}>
                    <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
                        <Field.Root 
                            required 
                            invalid={fieldStates.firstName.touched && fieldStates.firstName.invalid}
                        >
                            <Field.Label>First Name <Field.RequiredIndicator /></Field.Label>
                            <Input 
                                name="firstName"
                                bg="#FFFFFF" 
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="First Name"
                            />
                            {fieldStates.firstName.touched && fieldStates.firstName.invalid && (
                                <Field.ErrorText>{getErrorMessage('firstName')}</Field.ErrorText>
                            )}
                        </Field.Root>

                        <Field.Root 
                            required 
                            invalid={fieldStates.lastName.touched && fieldStates.lastName.invalid}
                        >
                            <Field.Label>Last Name <Field.RequiredIndicator /></Field.Label>
                            <Input 
                                name="lastName"
                                bg="#FFFFFF" 
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="Last Name"
                            />
                            {fieldStates.lastName.touched && fieldStates.lastName.invalid && (
                                <Field.ErrorText>{getErrorMessage('lastName')}</Field.ErrorText>
                            )}
                        </Field.Root>

                        <Field.Root 
                            required 
                            invalid={fieldStates.email.touched && fieldStates.email.invalid}
                        >
                            <Field.Label>Email <Field.RequiredIndicator /></Field.Label>
                            <Input 
                                name="email" 
                                type="email"
                                bg="#FFFFFF" 
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="Email Address"
                            />
                            {fieldStates.email.touched && fieldStates.email.invalid && (
                                <Field.ErrorText>{getErrorMessage('email')}</Field.ErrorText>
                            )}
                        </Field.Root>

                        <Field.Root 
                            required 
                            invalid={fieldStates.phone.touched && fieldStates.phone.invalid}
                        >
                            <Field.Label>Phone <Field.RequiredIndicator /></Field.Label>
                            <Input 
                                name="phone" 
                                type="tel"
                                bg="#FFFFFF" 
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="Local/International Phone Number"
                            />
                            {fieldStates.phone.touched && fieldStates.phone.invalid && (
                                <Field.ErrorText>{getErrorMessage('phone')}</Field.ErrorText>
                            )}
                        </Field.Root>

                        <Field.Root 
                            required 
                            invalid={fieldStates.carModel.touched && fieldStates.carModel.invalid}
                        >
                            <Field.Label>Car Model <Field.RequiredIndicator /></Field.Label>
                            <Input 
                                name="carModel"
                                bg="#FFFFFF" 
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="Car Model"
                            />
                            {fieldStates.carModel.touched && fieldStates.carModel.invalid && (
                                <Field.ErrorText>{getErrorMessage('carModel')}</Field.ErrorText>
                            )}
                        </Field.Root>

                        <Field.Root 
                            required 
                            invalid={fieldStates.yearModel.touched && fieldStates.yearModel.invalid}
                        >
                            <Field.Label>Year Model <Field.RequiredIndicator /></Field.Label>
                            <Input 
                                name="yearModel"
                                bg="#FFFFFF" 
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="Year Model"
                            />
                            {fieldStates.yearModel.touched && fieldStates.yearModel.invalid && (
                                <Field.ErrorText>{getErrorMessage('yearModel')}</Field.ErrorText>
                            )}
                        </Field.Root>
                    </Grid>

                    <Box mt={6}>
                        <Field.Root 
                            required 
                            invalid={fieldStates.message.touched && fieldStates.message.invalid}
                        >
                            <Field.Label>Enquiry / Requirements <Field.RequiredIndicator /></Field.Label>
                            <Textarea 
                                name="message" 
                                rows={5} 
                                bg="#FFFFFF" 
                                onBlur={handleBlur}
                                onChange={handleChange}
                                placeholder="Message"
                            />
                            {fieldStates.message.touched && fieldStates.message.invalid && (
                                <Field.ErrorText>{getErrorMessage('message')}</Field.ErrorText>
                            )}
                        </Field.Root>
                    </Box>

                    <Box mt={6}>
                        <ReCAPTCHA
                            sitekey="6LdpEyArAAAAAJuhKtKCHK-cH8Gz1lovbdxJ7do6"
                            ref={recaptchaRef}
                        />
                    </Box>

                    <Button
                        mt={6}
                        type="submit"
                        loading={isSubmitting}
                        disabled={isSubmitted || isSubmitting}
                    >
                        {isSubmitted ? "Submitted ✔️" : "Submit"}
                    </Button>
                </form>
                </Box>
            </Container>
        </Box>
    )
}